// src/components/RunningList.js - Public-facing Running List
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getClothNumber, getDancerName, getDancersList } from '../utils/fieldCompatibility';
import { getEventsWithStartTimes } from '../utils/startTimeCalculator';
import './RunningList.css';

const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

/**
 * Helper function to detect non-dance events
 * @param {Object} event - Event object
 * @returns {boolean} True if this is a non-dance event
 */
const isNonDanceEvent = (event) => {
  // Check explicit flag first (for future events created with the new form)
  if (event.isNonDanceEvent === true) {
    return true;
  }

  // Legacy detection: events with no dances, no age/level/style info are likely non-dance events
  const hasNoDances = (!event.dances || event.dances.length === 0);
  const hasNullOrMissingDanceInfo = (
    (event.ageGroup === null || event.ageGroup === undefined || event.ageGroup === "") &&
    (event.level === null || event.level === undefined || event.level === "") &&
    (event.danceStyle === null || event.danceStyle === undefined || event.danceStyle === "")
  );
  const hasNoDancerInfo = (!event.dancers || event.dancers.length === 0);

  // Also check if the name suggests it's a break/non-dance event
  const nameIndicatesNonDance = event.name && (
    event.name.toUpperCase().includes('BREAK') ||
    event.name.toUpperCase().includes('LUNCH') ||
    event.name.toUpperCase().includes('INTERVAL') ||
    event.name.toUpperCase().includes('ENTERTAINMENT') ||
    event.name.toUpperCase().includes('OPENING') ||
    event.name.toUpperCase().includes('CLOSING')
  );

  // If the name clearly indicates it's a non-dance event, prioritize that over dancer assignment
  if (nameIndicatesNonDance && hasNoDances) {
    return true;
  }

  return hasNoDances && hasNullOrMissingDanceInfo && hasNoDancerInfo;
};

// Dancer Card Component with Check-in Status
function DancerCard({ dancer, clubs, isHighlighted, isNameHighlighted, searchQuery, highlightText, isDancerMatch, isDancerNameMatch, getDancerLocation }) {
  const [dancerDetails, setDancerDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch complete dancer information including check-in status
  useEffect(() => {
    const fetchDancerDetails = async () => {
      if (!dancer.coupleId && !dancer.cloth_number && !dancer.clothNumber) {
        setLoading(false);
        return;
      }

      try {
        // Try to find dancer by coupleId first, then by cloth number
        let dancerId = dancer.coupleId;
        if (!dancerId) {
          // If no coupleId, try to find by cloth number
          const clothNumber = dancer.cloth_number || dancer.clothNumber;
          if (clothNumber) {
            const response = await fetch(`https://${_host}:6003/api/${_company}/couples`);
            if (response.ok) {
              const allCouples = await response.json();
              const foundDancer = allCouples.find(c =>
                (c.clothNumber === clothNumber || c.cloth_number === clothNumber) &&
                c.company === _company
              );
              if (foundDancer) {
                dancerId = foundDancer.id;
              }
            }
          }
        }

        if (dancerId) {
          const response = await fetch(`https://${_host}:6003/api/${_company}/couples/${dancerId}`);
          if (response.ok) {
            const dancerData = await response.json();
            setDancerDetails(dancerData);
          }
        }
      } catch (error) {
        console.error('Error fetching dancer details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDancerDetails();
  }, [dancer.coupleId, dancer.cloth_number, dancer.clothNumber]);

  // Get registration check-in status
  const getRegistrationStatus = () => {
    if (loading) {
      return <span className="status-loading">Loading...</span>;
    }

    // Use dancerDetails if available, otherwise fall back to event dancer data
    const regCheckin = dancerDetails?.registrationCheckin !== undefined
      ? dancerDetails.registrationCheckin
      : dancerDetails?.registration_checkin !== undefined
      ? dancerDetails.registration_checkin
      : dancer?.registrationCheckin
      || dancer?.registration_checkin;

    return (
      <span className={`checkin-status ${regCheckin === true ? 'checked-in' : 'not-checked-in'}`}>
        {regCheckin === true ? '✓ PRESENT' : '✗ NOT ARRIVED'}
      </span>
    );
  };

  // Get marshall check-in status
  const getMarshallStatus = () => {
    if (loading) {
      return <span className="status-loading">Loading...</span>;
    }

    // Use dancerDetails if available, otherwise fall back to event dancer data
    const marshallCheckin = dancerDetails?.marshallCheckin !== undefined
      ? dancerDetails.marshallCheckin
      : dancerDetails?.marshall_checkin !== undefined
      ? dancerDetails.marshall_checkin
      : dancer?.marshallCheckin
      || dancer?.marshall_checkin;

    return (
      <span className={`checkin-status ${marshallCheckin === true ? 'checked-in' : 'not-checked-in'}`}>
        {marshallCheckin === true ? '✓ READY' : '✗ NOT MARSHALLED'}
      </span>
    );
  };

  return (
    <div
      className={`dancer-card ${isHighlighted ? 'highlighted' : ''} ${isNameHighlighted ? 'name-highlighted' : ''}`}
    >
      <div className={`dancer-number ${isHighlighted ? 'highlighted-number' : ''}`}>
        {getClothNumber(dancer)}
      </div>
      <div className="dancer-info">
        <div className={`dancer-name ${isNameHighlighted ? 'search-highlight-text' : ''}`}>
          {searchQuery && isNameHighlighted ? highlightText(getDancerName(dancer), searchQuery) : getDancerName(dancer)}
        </div>
        <div className="dancer-country">{getDancerLocation(dancer)}</div>
        <div className="dancer-checkin-status">
          {getRegistrationStatus()}
          <span className="status-separator">•</span>
          {getMarshallStatus()}
        </div>
      </div>
    </div>
  );
}

const RunningList = () => {
  const { competitionId, clothNumber } = useParams();
  const [events, setEvents] = useState([]);
  const [currentDisplay, setCurrentDisplay] = useState(null);
  const [competitions, setCompetitions] = useState([]);
  const [activeCompetition, setActiveCompetition] = useState(null);
  const [competitionStartTime, setCompetitionStartTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [clubs, setClubs] = useState([]);
  const [dancerClubCache, setDancerClubCache] = useState(new Map());
  const [isOfficialsExpanded, setIsOfficialsExpanded] = useState(false);

  // Fetch clubs data for location lookup
  const fetchClubs = async () => {
    try {
      const response = await fetch(`https://${_host}:6003/api/${_company}/clubs`);
      if (response.ok) {
        const clubsData = await response.json();
        console.log('Running List - Loaded clubs:', clubsData);
        setClubs(clubsData);
        return clubsData;
      }
      return [];
    } catch (error) {
      console.error('Error fetching clubs:', error);
      return [];
    }
  };

  // Get dancer location based on club - enhanced synchronous lookup
  const getDancerLocation = (dancer) => {
    console.log('Running List - Getting location for dancer:', dancer.coupleId, 'Cache size:', dancerClubCache.size);

    // Check cache first
    if (dancerClubCache.has(dancer.coupleId)) {
      const cachedResult = dancerClubCache.get(dancer.coupleId);
      console.log('Running List - Found cached location for', dancer.coupleId, ':', cachedResult);
      return cachedResult;
    }

    // Enhanced inline lookup for when cache isn't built yet
    let location = dancer.country || 'Unknown';

    // Try to find club by clubId from dancer data
    let club = null;
    if (dancer.clubId) {
      club = clubs.find(c => c.id === dancer.clubId);
    }

    // Try to find club by clubName from dancer data
    if (!club && dancer.clubName) {
      club = clubs.find(c => c.name.toLowerCase() === dancer.clubName.toLowerCase());
    }

    // Use the same enhanced location logic as the cache builder
    if (club) {
      if (club.province && club.country) {
        // Club has both province and country - show both
        location = `${club.province}, ${club.country}`;
      } else if (club.province) {
        // Club has province but no country - combine with dancer's country if available
        const country = dancer.country || club.country || '';
        location = country ? `${club.province}, ${country}` : club.province;
      } else if (club.country) {
        // Club has country but no province - use club country (may be more specific than dancer country)
        location = club.country;
      } else if (club.city) {
        // Club has city but no province/country - combine with dancer's country
        const country = dancer.country || club.country || '';
        location = country ? `${club.city}, ${country}` : club.city;
      } else if (club.name) {
        // Club has name but no location details - use club name with dancer country
        const country = dancer.country || 'Unknown';
        location = `${club.name}, ${country}`;
      }
    } else {
      // No club found - try to use dancer's own location data if available
      if (dancer.city && dancer.country) {
        location = `${dancer.city}, ${dancer.country}`;
      } else if (dancer.province && dancer.country) {
        location = `${dancer.province}, ${dancer.country}`;
      } else if (dancer.city) {
        const country = dancer.country || 'Unknown';
        location = `${dancer.city}, ${country}`;
      } else if (dancer.province) {
        const country = dancer.country || 'Unknown';
        location = `${dancer.province}, ${country}`;
      }
      // Fallback to dancer country (already set as default)
    }

    console.log('Running List - No cache entry for', dancer.coupleId, ', calculated location:', location);
    return location;
  };

  // Build dancer location cache from all couples data (called once)
  const buildDancerLocationCache = async () => {
    if (clubs.length === 0) return;

    try {
      console.log('Running List - Building dancer location cache from all couples...');
      const response = await fetch(`https://${_host}:6003/api/${_company}/couples`);
      if (response.ok) {
        const allCouples = await response.json();
        console.log('Running List - Loaded', allCouples.length, 'couples for location cache');

        const newCache = new Map();

        allCouples.forEach(couple => {
          let location = couple.country || 'Unknown';

          // Try to find club by clubId
          let club = null;
          if (couple.clubId) {
            club = clubs.find(c => c.id === couple.clubId);
          }

          // Try to find club by clubName
          if (!club && couple.clubName) {
            club = clubs.find(c => c.name.toLowerCase() === couple.clubName.toLowerCase());
          }

          // Enhanced location logic - prioritize showing both province and country from club/studio
          if (club) {
            if (club.province && club.country) {
              // Club has both province and country - show both
              location = `${club.province}, ${club.country}`;
            } else if (club.province) {
              // Club has province but no country - combine with dancer's country if available
              const country = couple.country || club.country || '';
              location = country ? `${club.province}, ${country}` : club.province;
            } else if (club.country) {
              // Club has country but no province - use club country (may be more specific than dancer country)
              location = club.country;
            } else if (club.city) {
              // Club has city but no province/country - combine with dancer's country
              const country = couple.country || club.country || '';
              location = country ? `${club.city}, ${country}` : club.city;
            } else if (club.name) {
              // Club has name but no location details - use club name with dancer country
              const country = couple.country || 'Unknown';
              location = `${club.name}, ${country}`;
            }
          } else {
            // No club found - try to use dancer's own location data if available
            if (couple.city && couple.country) {
              location = `${couple.city}, ${couple.country}`;
            } else if (couple.province && couple.country) {
              location = `${couple.province}, ${couple.country}`;
            } else if (couple.city) {
              const country = couple.country || 'Unknown';
              location = `${couple.city}, ${country}`;
            } else if (couple.province) {
              const country = couple.country || 'Unknown';
              location = `${couple.province}, ${country}`;
            }
            // Fallback to dancer country (already set as default)
          }

          // Cache the result
          newCache.set(couple.id, location);
        });

        console.log('Running List - Built location cache with', newCache.size, 'entries');
        setDancerClubCache(newCache);
      }
    } catch (error) {
      console.error('Running List - Error building dancer location cache:', error);
    }
  };

  // Fetch events, current display, and competition settings
  const fetchData = async () => {
    try {
      // Fetch competitions, events, display, backgrounds, and clubs in parallel
      const [competitionsResponse, eventsResponse, displayResponse, backgroundsResponse, clubsResponse] = await Promise.all([
        fetch(`https://${_host}:6003/api/${_company}/competitions`),
        fetch(`https://${_host}:6003/api/${_company}/events`),
        fetch(`https://${_host}:6003/api/${_company}/display`),
        fetch(`https://${_host}:6003/api/${_company}/backgrounds`),
        fetch(`https://${_host}:6003/api/${_company}/clubs`)
      ]);

      let competitionsData = [];
      let activeComp = null;

      // Handle competitions data
      if (competitionsResponse.ok) {
        competitionsData = await competitionsResponse.json();
        setCompetitions(competitionsData);

        // Priority: 1. URL competition (always use if provided), 2. Active competition, 3. First competition
        if (competitionId) {
          activeComp = competitionsData.find(comp => comp.id === competitionId);
          // If URL competition ID is provided but not found, still try to use it for cloth number search
          if (!activeComp && clothNumber) {
            // Set up with a fallback competition structure for cloth number filtering
            activeComp = {
              id: competitionId,
              name: 'Competition',
              isActive: false
            };
          }
        }
        if (!activeComp) {
          activeComp = competitionsData.find(comp => comp.isActive);
        }
        if (!activeComp) {
          activeComp = competitionsData[0];
        }
        if (activeComp) {
          setActiveCompetition(activeComp);

          // Fetch competition details including start time
          try {
            const compDetailsResponse = await fetch(`https://${_host}:6003/api/${_company}/competitions/${activeComp.id}`);
            if (compDetailsResponse.ok) {
              const compDetails = await compDetailsResponse.json();

              // Check for different possible field names
              const startTimeField = compDetails.official_start_time || compDetails.officialStartTime || compDetails.start_time;

              if (startTimeField) {
                const startTime = new Date(startTimeField);
                setCompetitionStartTime(startTime);
              } else {
                // Use default start time: 9:00 AM today
                const defaultStartTime = new Date();
                defaultStartTime.setHours(9, 0, 0, 0);
                setCompetitionStartTime(defaultStartTime);
              }
            } else {
              // Use default start time: 9:00 AM today
              const defaultStartTime = new Date();
              defaultStartTime.setHours(9, 0, 0, 0);
              setCompetitionStartTime(defaultStartTime);
            }
          } catch (error) {
            console.error('Running List - Error fetching competition start time:', error);
            // Use default start time: 9:00 AM today
            const defaultStartTime = new Date();
            defaultStartTime.setHours(9, 0, 0, 0);
            setCompetitionStartTime(defaultStartTime);
          }
        }
      }

      // Handle events data
      if (eventsResponse.ok) {
        const eventsData = await eventsResponse.json();

        // Filter events by competition ID - use URL competitionId if provided, otherwise use activeComp.id
        if (competitionId) {
          // Always use the URL competitionId for filtering when provided
          const competitionEvents = eventsData.filter(event =>
            event.competitionId === competitionId
          );
          setEvents(competitionEvents);
        } else if (activeComp) {
          // Filter events to only show those from the selected competition
          const competitionEvents = eventsData.filter(event =>
            event.competitionId === activeComp.id
          );
          setEvents(competitionEvents);
        } else {
          setEvents(eventsData);
        }
      }

      // Handle display data
      if (displayResponse.ok) {
        const displayData = await displayResponse.json();
        setCurrentDisplay(displayData);
      }

      // Handle backgrounds for logo
      if (backgroundsResponse.ok) {
        const backgroundsData = await backgroundsResponse.json();
        // Look for primary logo first
        const primaryLogo = backgroundsData.find(bg =>
          bg.name && bg.name.toLowerCase() === 'logo'
        );
        // Fallback to any image with logo in the name
        const fallbackLogo = primaryLogo || backgroundsData.find(bg =>
          bg.name && bg.name.toLowerCase().includes('logo')
        );
      }

      // Handle clubs data for location lookup
      if (clubsResponse.ok) {
        const clubsData = await clubsResponse.json();
        setClubs(clubsData);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load running order data. Please try again later.');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Build location cache after initial data load
    const timer = setTimeout(() => {
      buildDancerLocationCache();
    }, 2000); // Wait 2 seconds after initial load

    // Set up polling for real-time updates
    const interval = setInterval(fetchData, 3000); // Poll every 3 seconds

    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, [competitionId]); // Re-fetch when competition ID changes

  // Rebuild location cache when clubs data changes
  useEffect(() => {
    if (clubs.length > 0) {
      console.log('Running List - Clubs data updated, rebuilding location cache');
      buildDancerLocationCache();
    }
  }, [clubs]);

  
  // Handle URL parameters for cloth number and competition
  useEffect(() => {
    if (clothNumber) {
      setSearchQuery(clothNumber);
    }
  }, [clothNumber]);

  // Handle competition selection from URL
  useEffect(() => {
    if (competitionId && competitions.length > 0) {
      const urlCompetition = competitions.find(comp => comp.id === competitionId);
      if (urlCompetition) {
        setActiveCompetition(urlCompetition);
      }
    }
  }, [competitionId, competitions]);

  // Toggle dancer expansion for an event
  const toggleDancers = (eventId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId);
    } else {
      newExpanded.add(eventId);
    }
    setExpandedItems(newExpanded);
  };

  // Filter events based on search query and track cloth number matches
  const filteredEvents = events.filter(event => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase().trim();
    const itemNumber = (event.itemNumber || (events.indexOf(event) + 1)).toString();

    // Search by item number (exact match or partial)
    if (itemNumber.includes(query)) return true;

    // Enhanced event name search - handles partial matches and case insensitive
    if (event.name) {
      const eventName = event.name.toLowerCase();
      // Check for exact match, partial match, or word boundaries
      if (eventName.includes(query) ||
          eventName.startsWith(query) ||
          eventName.endsWith(query) ||
          eventName.split(' ').some(word => word.includes(query))) {
        return true;
      }
    }

    // Search by round type (e.g., "quarter", "semi", "final")
    if (event.roundType && event.roundType.toLowerCase().includes(query)) {
      return true;
    }

    // Search by dance styles if available
    if (event.dances && Array.isArray(event.dances)) {
      const danceStyles = event.dances.join(' ').toLowerCase();
      if (danceStyles.includes(query)) {
        return true;
      }
    }

    // Search by cloth number in dancers
    const dancers = getDancersList(event);
    if (dancers && dancers.length > 0) {
      return dancers.some(dancer => {
        // Search by cloth number
        const dancerClothNumber = getClothNumber(dancer);
        if (dancerClothNumber && dancerClothNumber.toLowerCase().includes(query)) {
          return true;
        }
        // Search by dancer name
        if (dancer.name && dancer.name.toLowerCase().includes(query)) {
          return true;
        }
        return false;
      });
    }

    return false;
  });

  // Function to check if a dancer matches the search query
  const isDancerMatch = (dancer) => {
    if (!searchQuery.trim()) return false;
    const query = searchQuery.toLowerCase().trim();

    // Search by cloth number
    const dancerClothNumber = getClothNumber(dancer);
    if (dancerClothNumber && dancerClothNumber.toLowerCase().includes(query)) {
      return true;
    }

    // Search by dancer name
    if (dancer.name && dancer.name.toLowerCase().includes(query)) {
      return true;
    }

    return false;
  };

  // Function to check if dancer name matches search query
  const isDancerNameMatch = (dancer) => {
    if (!searchQuery.trim()) return false;
    const query = searchQuery.toLowerCase().trim();
    return dancer.name && dancer.name.toLowerCase().includes(query);
  };

  // Function to highlight matching text in event names
  const highlightText = (text, query) => {
    if (!query.trim() || !text) return text;

    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ?
        <span key={index} className="search-highlight">{part}</span> : part
    );
  };

  // Function to determine what type of match an event has
  const getMatchType = (event, query) => {
    if (!query.trim()) return null;

    const queryLower = query.toLowerCase();

    // Check item number match
    const itemNumber = (event.itemNumber || (events.indexOf(event) + 1)).toString();
    if (itemNumber.includes(queryLower)) return 'item';

    // Check event name match
    if (event.name && event.name.toLowerCase().includes(queryLower)) return 'name';

    // Check round type match
    if (event.roundType && event.roundType.toLowerCase().includes(queryLower)) return 'round';

    // Check dance styles match
    if (event.dances && Array.isArray(event.dances)) {
      const danceStyles = event.dances.join(' ').toLowerCase();
      if (danceStyles.includes(queryLower)) return 'dance';
    }

    // Check dancer match
    const dancersList = getDancersList(event);
    if (dancersList && dancersList.length > 0) {
      const hasDancerMatch = dancersList.some(dancer => {
        const dancerClothNumber = getClothNumber(dancer);
        return (dancerClothNumber && dancerClothNumber.toLowerCase().includes(queryLower)) ||
               (dancer.name && dancer.name.toLowerCase().includes(queryLower));
      });
      if (hasDancerMatch) return 'dancer';
    }

    return null;
  };

  // Sort events by item number
  const sortedEvents = [...filteredEvents].sort((a, b) => {
    const itemA = a.itemNumber || 999;
    const itemB = b.itemNumber || 999;
    return itemA - itemB;
  });

  // Calculate start times if competition start time is available
  const eventsWithStartTimes = competitionStartTime
    ? getEventsWithStartTimes(sortedEvents, competitionStartTime)
    : sortedEvents;

  // Auto-expand events that have matching cloth numbers
  useEffect(() => {
    if (searchQuery.trim()) {
      const eventsWithMatchingDancers = new Set();
      eventsWithStartTimes.forEach(event => {
        const dancers = getDancersList(event);
        if (dancers && dancers.length > 0) {
          const hasMatchingDancer = dancers.some(dancer => isDancerMatch(dancer));
          if (hasMatchingDancer) {
            eventsWithMatchingDancers.add(event.id);
          }
        }
      });

      if (eventsWithMatchingDancers.size > 0) {
        setExpandedItems(prev => new Set([...prev, ...eventsWithMatchingDancers]));
      }
    }
  }, [searchQuery, eventsWithStartTimes]);

  // Check if an event is currently running
  const isEventRunning = (event) => {
    return currentDisplay &&
           event.id === currentDisplay.eventId &&
           event.active;
  };

  if (loading) {
    return (
      <div className="running-list-container">
        <div className="loading">Loading running order...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="running-list-container">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="running-list-container">
      {/* Combined Fixed Header */}
      <div className="fixed-header">
        <div className="header-top">
          <div className="header-content">
            {/* Logo - positioned above competition name */}
            {activeCompetition?.thumbnail ? (
              <img
                src={activeCompetition.thumbnail}
                alt={`${activeCompetition.name} Logo`}
                className="competition-logo"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                  console.warn('Competition thumbnail not found, fallback to trophy icon');
                }}
              />
            ) : (
              <div className="competition-icon">🏆</div>
            )}

            {/* Competition Name - positioned below logo */}
            <div className="header-info">
              <h1>{activeCompetition?.name || 'DanceSport Competition'}</h1>
              <p className="subtitle">Running Order</p>
            </div>
          </div>
        </div>

        {/* Dance Sport Officials Section */}
        <div className="officials-section">
          <div
            className="officials-header"
            onClick={() => setIsOfficialsExpanded(!isOfficialsExpanded)}
          >
            <span className="officials-title">Dance Sport Officials</span>
            <span className={`officials-toggle ${isOfficialsExpanded ? 'expanded' : ''}`}>
              {isOfficialsExpanded ? '▼' : '▶'}
            </span>
          </div>
          {isOfficialsExpanded && (
            <div className="officials-content">
              <div className="officials-grid">
                <div className="official-item">
                  <div className="official-role">Chair of Adjudicators</div>
                  <div className="official-name">Owen Nene</div>
                </div>
                <div className="official-item">
                  <div className="official-role">Adjudicator</div>
                  <div className="official-name">Mavis Jacobs</div>
                </div>
                <div className="official-item">
                  <div className="official-role">Adjudicator</div>
                  <div className="official-name">Cookie Chengleroyan</div>
                </div>
                <div className="official-item">
                  <div className="official-role">Adjudicator</div>
                  <div className="official-name">Michelle Van Deventer</div>
                </div>
                <div className="official-item">
                  <div className="official-role">Adjudicator</div>
                  <div className="official-name">Richman Nhlapho</div>
                </div>
                <div className="official-item">
                  <div className="official-role">Adjudicator</div>
                  <div className="official-name">Tebogo Kgobokoe</div>
                </div>
                <div className="official-item">
                  <div className="official-role">Scrutineer</div>
                  <div className="official-name">Sanele Gumede</div>
                </div>
                <div className="official-item">
                  <div className="official-role">Programme Director</div>
                  <div className="official-name">Boniswa Gazu</div>
                </div>
                <div className="official-item">
                  <div className="official-role">Music Director</div>
                  <div className="official-name">Cya Maphumulo</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Search Bar in Header */}
        <div className="header-search">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search by item number, event name, dance style, round, cloth number, or dancer name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button
                className="search-clear-btn"
                onClick={() => setSearchQuery('')}
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>
          {searchQuery && (
            <div className="search-results-info">
              <span className="search-stats">
                Found {sortedEvents.length} of {events.length} events
              </span>
              {sortedEvents.length < events.length && (
                <span className="search-hint">💡 Try different search terms</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Scrollable Content - Only Events List */}
      <div className="scrollable-content">
        {/* Events List */}
        {sortedEvents.length === 0 ? (
          <div className="no-events">
            {searchQuery ? (
              <div>
                <p>No events found matching "{searchQuery}"</p>
                <button onClick={() => setSearchQuery('')} className="clear-search-btn">
                  Clear search
                </button>
              </div>
            ) : (
              <p>No events scheduled</p>
            )}
          </div>
        ) : (
          <div className="events-list">
            {eventsWithStartTimes.map((event, index) => {
              const isRunning = isEventRunning(event);
              const isExpanded = expandedItems.has(event.id);
              const itemNumber = event.itemNumber || (index + 1);
              const matchType = getMatchType(event, searchQuery);

              return (
                <div
                  key={event.id}
                  className={`event-item ${isRunning ? 'running' : ''} ${isExpanded ? 'expanded' : ''} ${matchType ? 'search-match' : ''} ${isNonDanceEvent(event) ? 'non-dance-event' : ''}`}
                >
                  {isNonDanceEvent(event) ? (
                    /* Non-Dance Event Layout */
                    <div
                      className="event-header non-dance-header"
                      onClick={() => toggleDancers(event.id)}
                    >
                      <div className="event-number">
                        <span className={`number ${matchType === 'item' ? 'search-highlight-number' : ''}`}>#{itemNumber}</span>
                        {isRunning && <span className="live-badge">RUNNING</span>}
                      </div>
                      <div className="non-dance-event-info">
                        <h3 className="non-dance-event-name">
                          {searchQuery ? highlightText(event.name.toUpperCase(), searchQuery) : event.name.toUpperCase()}
                        </h3>
                      </div>
                      <div className="expand-button">
                        <span className={`arrow ${isExpanded ? 'up' : 'down'}`}>
                          {isExpanded ? '▼' : '▶'}
                        </span>
                      </div>
                    </div>
                  ) : (
                    /* Regular Dance Event Layout */
                    <div
                      className="event-header"
                      onClick={() => toggleDancers(event.id)}
                    >
                      <div className="event-number">
                        <span className={`number ${matchType === 'item' ? 'search-highlight-number' : ''}`}>#{itemNumber}</span>
                        {isRunning && <span className="live-badge">RUNNING</span>}
                      </div>
                      <div className="event-info">
                        <h3 className="event-name">
                          {searchQuery ? highlightText(event.name, searchQuery) : event.name}
                        </h3>
                        <div className="event-details">
                          <span className={`round-type ${matchType === 'round' ? 'search-highlight-type' : ''}`}>
                            {searchQuery && matchType === 'round' ? highlightText(event.roundType, searchQuery) : event.roundType}
                          </span>
                          {event.dances && Array.isArray(event.dances) && matchType === 'dance' && (
                            <span className="dance-match-indicator">
                              Dance match: {highlightText(event.dances.join(', '), searchQuery)}
                            </span>
                          )}
                          <span className="dancer-count">{getDancersList(event)?.length || 0} Dancers</span>
                          {matchType === 'dancer' && (
                            <span className="dancer-match-indicator">👤 Dancer match</span>
                          )}
                        </div>
                      </div>
                      <div className="expand-button">
                        <span className={`arrow ${isExpanded ? 'up' : 'down'}`}>
                          {isExpanded ? '▼' : '▶'}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Expanded Dancers Section - Only for regular dance events */}
                  {!isNonDanceEvent(event) && (() => {
                    const dancers = getDancersList(event);
                    return isExpanded && dancers && dancers.length > 0;
                  })() && (
                    <div className="dancers-section">
                      <div className="dancers-header">
                        <h4>Allocated Dancers</h4>
                      </div>
                      <div className="dancers-grid">
                        {getDancersList(event).map((dancer, dancerIndex) => (
                          <DancerCard
                            key={dancerIndex}
                            dancer={dancer}
                            clubs={clubs}
                            isHighlighted={isDancerMatch(dancer)}
                            isNameHighlighted={isDancerNameMatch(dancer)}
                            searchQuery={searchQuery}
                            highlightText={highlightText}
                            isDancerMatch={isDancerMatch}
                            isDancerNameMatch={isDancerNameMatch}
                            getDancerLocation={getDancerLocation}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Expanded Empty Section - Only for regular dance events */}
                  {!isNonDanceEvent(event) && (() => {
                    const dancers = getDancersList(event);
                    return isExpanded && (!dancers || dancers.length === 0);
                  })() && (
                    <div className="dancers-section empty">
                      <p>No dancers allocated to this event</p>
                    </div>
                  )}

                  {/* Non-Dance Event Message - Show when expanded */}
                  {isNonDanceEvent(event) && isExpanded && (
                    <div className="non-dance-expanded-section">
                      <div className="non-dance-message">
                        <p>This is a non-dance event with a duration of {event.customDuration || 15} minutes</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Fixed Footer */}
      <div className="running-list-footer">
        <p className="footer-text">Last updated: {new Date().toLocaleTimeString()}</p>
        <p className="footer-info">Public Running List • Powered by Flokraft</p>
      </div>
    </div>
  );
};

export default RunningList;